import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useAdminMessagingSSE } from "@/hooks/useAdminMessagingSSE";

export interface AdminMessagingContextValue {
  /** Number of threads with unread messages. */
  unreadThreadCount: number;
  /** Per-thread unread counts (threadId -> count). */
  unreadByThread: Record<string, number>;
}

const AdminMessagingContext = createContext<AdminMessagingContextValue | undefined>(
  undefined
);

// eslint-disable-next-line react-refresh/only-export-components
export function useAdminMessaging(): AdminMessagingContextValue {
  const ctx = useContext(AdminMessagingContext);
  if (!ctx) {
    throw new Error("useAdminMessaging must be used within AdminMessagingProvider");
  }
  return ctx;
}

export function AdminMessagingProvider({ children }: { children: ReactNode }) {
  const { unreadThreadCount, unreadByThread } = useAdminMessagingSSE();

  const value = useMemo<AdminMessagingContextValue>(
    () => ({ unreadThreadCount, unreadByThread }),
    [unreadThreadCount, unreadByThread]
  );

  return (
    <AdminMessagingContext.Provider value={value}>
      {children}
    </AdminMessagingContext.Provider>
  );
}
